import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { getAnalysisById } from '../utils/api';

const TYPE_LABEL = {
  xray: 'Chest X-Ray',
  breast: 'Breast Ultrasound',
  skin: 'Skin',
  retina: 'Retinal Fundus',
  foot: 'Diabetic Foot',
};

const NORMAL_RISK_TIERS = new Set(['normal', 'benign', 'none']);

const getColorForRisk = (risk) => {
  const r = (risk || '').toLowerCase();
  if (r === 'malignant' || r === 'critical' || r === 'high') return '#FF3366';
  if (r === 'moderate' || r === 'pre-malignant') return '#FFCC00';
  return '#00CCFF';
};

const AnalysisDetail = () => {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [activeCondition, setActiveCondition] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getAnalysisById(id, currentUser?.token);
        if (cancelled) return;
        setAnalysis(data);
        setActiveCondition(data.predictions?.[0]?.label || null);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load this analysis');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, currentUser]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-black text-white">
        <Navbar />
        <main className="flex-grow flex items-center justify-center text-gray-400">Loading analysis…</main>
        <Footer />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen flex flex-col bg-black text-white">
        <Navbar />
        <main className="flex-grow flex flex-col items-center justify-center text-gray-400 gap-4">
          <p>{error || 'Analysis not found'}</p>
          <Link to="/analysis" className="text-purple-400 hover:text-purple-300 flex items-center">
            <FaArrowLeft className="mr-2" /> Back to Analyses
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const predictions = analysis.predictions || [];
  const top = predictions[0];
  const roi = analysis.region_of_interest;
  const isNormal = !top || NORMAL_RISK_TIERS.has((top.risk || '').toLowerCase()) || (top.label || '').toLowerCase() === 'no finding';
  const dateLabel = analysis.date
    ? new Date(analysis.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
    : 'Unknown date';
  const typeLabel = TYPE_LABEL[analysis.type] || analysis.type;

  const heatmapDataUrl = () => {
    if (!roi) return null;
    const color = getColorForRisk(top?.risk);
    const svg = `
      <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="heatGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.6" />
            <stop offset="70%" stop-color="${color}" stop-opacity="0.35" />
            <stop offset="100%" stop-color="${color}" stop-opacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="${roi.x}%" cy="${roi.y}%" rx="${roi.radius}%" ry="${roi.radius}%" fill="url(#heatGradient)" />
      </svg>
    `;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-black text-white">
      <Navbar />
      <main className="flex-grow py-8 px-4 sm:px-6 lg:px-8 page-transition">
        <div className="container-custom">
          <div className="flex items-center mb-8">
            <Link to="/analysis" className="text-purple-400 hover:text-purple-300 mr-4 flex items-center">
              <FaArrowLeft className="mr-2" />
              Back to Analyses
            </Link>
            <h1 className="heading-secondary">{typeLabel} Analysis</h1>
          </div>

          {/* Status Bar */}
          <div className="bg-gray-900 rounded-xl p-4 mb-6 flex justify-between items-center border border-gray-800 flex-wrap gap-4">
            <div className="flex items-center space-x-6 flex-wrap gap-y-2">
              <div>
                <span className="text-sm text-gray-400">Date:</span>
                <span className="ml-2 text-white">{dateLabel}</span>
              </div>
              {analysis.patient_context?.age != null && (
                <div>
                  <span className="text-sm text-gray-400">Age/Sex:</span>
                  <span className="ml-2 text-white">{analysis.patient_context.age}y{analysis.patient_context.sex ? `, ${analysis.patient_context.sex}` : ''}</span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                analysis.review_status === 'pending_review' ? 'bg-yellow-900/30 text-yellow-400 border border-yellow-500/30' :
                analysis.review_status === 'verified' ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                'bg-blue-900/30 text-blue-400 border border-blue-500/30'
              }`}>
                {analysis.review_status === 'pending_review' ? 'Pending Clinician Review' : analysis.review_status}
              </div>
              <div className={`px-3 py-1 text-xs font-medium rounded-full ${
                isNormal ? 'bg-green-900/30 text-green-400 border border-green-500/30' :
                'bg-red-900/30 text-red-400 border border-red-500/30'
              }`}>
                {isNormal ? 'Normal' : 'Abnormal'}
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-8">
            {/* Left Column */}
            <div className="md:col-span-5 space-y-8">
              {/* AI Analysis Results */}
              <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
                <div className="p-5 border-b border-gray-800 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
                  <h2 className="heading-subsection-gradient flex items-center">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-indigo-400">AI</span>
                    <span className="ml-1 text-white">Analysis Results</span>
                  </h2>
                </div>
                <div className="p-5">
                  <div className="mb-6">
                    <h3 className="text-sm text-purple-400 mb-2">Leading Finding</h3>
                    <p className="text-white">
                      {top ? `${top.label} (risk tier: ${top.risk || 'unknown'})` : 'No prediction recorded for this analysis.'}
                    </p>
                  </div>

                  {top && (
                    <div className="mb-6">
                      <div className="flex justify-between mb-1">
                        <h3 className="text-sm text-purple-400">AI Confidence</h3>
                        <span className="text-sm text-gray-400">{Math.round((top.confidence || 0) * 100)}%</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${
                            top.confidence > 0.7 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                            top.confidence > 0.4 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                            'bg-gradient-to-r from-red-500 to-red-600'
                          }`}
                          style={{ width: `${Math.round((top.confidence || 0) * 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {analysis.recommendations?.length > 0 && (
                    <div className="bg-gray-800/70 p-4 rounded-lg border-l-2 border-purple-500">
                      <h3 className="text-sm text-purple-400 mb-3">Recommendations</h3>
                      <ul className="text-white space-y-1 list-disc list-inside">
                        {analysis.recommendations.map((rec, i) => <li key={i}>{rec}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {analysis.reviewed_by && (
                <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
                  <div className="p-5 border-b border-gray-800 flex justify-between items-center bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
                    <h2 className="heading-subsection">Clinician Sign-off</h2>
                    <FaInfoCircle className="text-purple-400" />
                  </div>
                  <div className="p-5 space-y-2">
                    <p className="text-white font-medium">{analysis.reviewed_by.name}</p>
                    <p className="text-sm text-gray-400">{analysis.reviewed_by.email}</p>
                    {analysis.review_notes && <p className="text-white mt-2">{analysis.review_notes}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column */}
            <div className="md:col-span-7 space-y-8">
              {/* Image Viewer with AI Heatmap */}
              {analysis.image_url && (
                <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
                  <div className="p-5 border-b border-gray-800 flex justify-between items-center">
                    <h2 className="heading-subsection">{typeLabel}</h2>
                    {roi && (
                      <button
                        onClick={() => setShowHeatmap(!showHeatmap)}
                        className={`px-3 py-1.5 rounded text-xs cursor-pointer ${showHeatmap
                          ? 'bg-purple-800 text-purple-100'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'} transition-colors flex items-center`}
                      >
                        {showHeatmap ? 'Hide AI Overlay' : 'Show AI Overlay'}
                      </button>
                    )}
                  </div>

                  <div className="bg-black flex justify-center items-center p-2 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-transparent to-indigo-900/30"></div>
                    <div className="relative w-full max-h-[400px] flex justify-center z-10">
                      <img
                        src={analysis.image_url}
                        alt={typeLabel}
                        className="max-h-[400px] object-contain"
                      />
                      {showHeatmap && roi && (
                        <div
                          className="absolute inset-0 bg-cover bg-center mix-blend-screen"
                          style={{ backgroundImage: `url("${heatmapDataUrl()}")` }}
                        />
                      )}
                      {showHeatmap && roi && (
                        <div className="absolute top-2 right-2 bg-black/70 text-white text-sm px-3 py-1 rounded-full flex items-center z-20">
                          <span className="h-2 w-2 rounded-full bg-green-500 mr-2 pulse-animation"></span>
                          <span>AI Analysis Complete</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="p-3 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-center text-sm text-gray-400">
                    {typeLabel} - {dateLabel}
                  </div>
                </div>
              )}

              {/* Detailed Findings */}
              <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
                <div className="p-5 border-b border-gray-800 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
                  <h2 className="heading-subsection">Detailed Findings</h2>
                </div>
                <div className="p-5">
                  {predictions.length === 0 && <p className="text-gray-400 text-sm">No predictions recorded for this analysis.</p>}
                  <div className="space-y-4">
                    {predictions.map((finding, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg flex items-start transition-all duration-300 cursor-pointer ${
                          activeCondition === finding.label
                            ? 'bg-purple-900/20 border-l-2 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.15)]'
                            : 'bg-gray-800/50 hover:bg-gray-800'
                        }`}
                        onClick={() => setActiveCondition(finding.label)}
                      >
                        <div className="pt-1">
                          <div className="h-3 w-3 rounded-full" style={{ backgroundColor: getColorForRisk(finding.risk) }}></div>
                        </div>
                        <div className="ml-4 flex-grow">
                          <div className="flex justify-between">
                            <span className="text-white font-medium">{finding.label}</span>
                            <span className="text-sm text-gray-400 capitalize">{finding.risk || 'unknown'}</span>
                          </div>
                          <div className="mt-2 flex items-center">
                            <div className="w-full bg-gray-700 rounded-full h-1.5 mr-3">
                              <div
                                className="h-1.5 rounded-full"
                                style={{
                                  width: `${Math.round((finding.confidence || 0) * 100)}%`,
                                  background: `linear-gradient(to right, ${getColorForRisk(finding.risk)}99, ${getColorForRisk(finding.risk)})`,
                                }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-300 w-10">{Math.round((finding.confidence || 0) * 100)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Analysis Metadata */}
              <div className="bg-gray-900 rounded-2xl shadow-md overflow-hidden border border-gray-800">
                <div className="p-5 border-b border-gray-800 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900">
                  <h2 className="heading-subsection">Analysis Information</h2>
                </div>
                <div className="p-5">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-400">Analysis ID</p>
                      <p className="text-white font-medium break-all">{analysis.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Analysis Date</p>
                      <p className="text-white font-medium">{dateLabel}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AnalysisDetail;
